import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';
import { HttpService } from './http.service';
import { ApiService } from './api.service';
import * as _ from 'lodash';
import { CreativeAnalysisImage, CreativeAnalysisVideo, CreativeAnalysisCarousel, CreativeStyle } from '../interfaces/creative-analysis';

@Injectable({
  providedIn: 'root'
})
export class CreativeAnalysisService {
    // Dependency Injection
    private apiService: ApiService = inject(ApiService);
    private httpService: HttpService = inject(HttpService);

    s3UploadUrl: string | null = null;

    // Track if the service is currently loading the s3UploadUrl to prevent firing multiple concurrent calls to the API
    loadingS3UploadUrl = false;

    constructor() { 
        console.log("[CreativeService] Initialising CreativeService");
    }

    // List all creatives
    async listCreatives(acc_id: string, type: 'img' | 'vid' | 'car', limit: number = 100, mode?: string) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] listCreatives()");
            let requestPath = `/creative-analysis/list-creatives?acc_id=${acc_id}&type=${type}&limit=${limit}`;
            if(mode) requestPath += `&mode=${mode}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] List response", _.size(response));
                // console.log("[CreativeService] List response", response);
                let creatives: CreativeAnalysisImage[] | CreativeAnalysisVideo[] | CreativeAnalysisCarousel[] = [];
                if(type == 'img') {
                    // IMGs
                    _.forEach(response.data, data => {
                        let image: CreativeAnalysisImage = {
                            id: data.id,
                            thumbnail: data.thumbnail,
                            url: data.url,
                            mainAngle: data.main_angle,
                            creativeStyle: data.creative_style,
                            creativeFormat: data.creative_format,
                            targetMarket: data.target_market,
                            marketAwareness: data.market_awareness,
                            usps: data.usps,
                            discounts: data.discounts,
                            headline: data.headline,
                            personFeature: data.person_feature,
                            callToAction: data.call_to_action,
                            needsQa: data.needs_qa,
                            needsExpertReview: data.needs_expert_review,
                            needsExpertReviewReason: data.needs_expert_review_reason
                        }
                        creatives.push(image);
                    });                        
                } else if(type == 'vid') {
                    // VIDs
                    _.forEach(response.data, data => {
                        let image: CreativeAnalysisVideo = {
                            id: data.id,
                            thumbnail: data.thumbnail,
                            url: data.url,
                            mainAngle: data.main_angle,
                            creativeStyle: data.creative_style,
                            creativeFormat: data.creative_format,
                            targetMarket: data.target_market,
                            marketAwareness: data.market_awareness,
                            usps: data.usps,
                            discounts: data.discounts,
                            ugc: data.ugc,
                            audioHook: data.audio_hook,
                            visualHook: data.visual_hook,
                            textHook: data.text_hook,
                            creator: data.creator,
                            ageGroup: data.age_group,
                            gender: data.gender,
                            length: data.length,
                            audioClose: data.audio_close,
                            visualClose: data.visual_close,
                            textClose: data.text_close,                            
                            needsQa: data.needs_qa,
                            needsExpertReview: data.needs_expert_review,
                            needsExpertReviewReason: data.needs_expert_review_reason
                        }
                        creatives.push(image);
                    }); 
                } else if(type == 'car') {
                    // CARs
                    _.forEach(response.data, data => {
                        let image: CreativeAnalysisCarousel = {
                            id: data.id,
                            thumbnail: data.thumbnail,
                            url: data.url,
                            mainAngle: data.main_angle,
                            creativeStyle: data.creative_style,
                            creativeFormat: data.creative_format,
                            targetMarket: data.target_market,
                            marketAwareness: data.market_awareness,
                            usps: data.usps,
                            discounts: data.discounts,
                            ugc: data.ugc,
                            numberOfTiles: data.number_of_tiles,
                            needsQa: data.needs_qa,
                            needsExpertReview: data.needs_expert_review,
                            needsExpertReviewReason: data.needs_expert_review_reason
                        }
                        creatives.push(image);
                    }); 
                } else {
                    // Return error
                    console.log("[CreativeService] List error, Invalid creative type");
                    reject("Invalid creative type");
                }
                resolve(creatives);
            })
            .catch(error => {
                console.log("[CreativeService] List error", error);
                reject(error.name);
            });
        });
    }

    // Export list of creatives
    async exportCreatives(acc_id: string, type: 'img' | 'vid' | 'car', limit: number = 5000) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] exportCreatives()");
            let requestPath = `/creative-analysis/list-creatives?acc_id=${acc_id}&type=${type}&limit=${limit}&mode=full&export=true`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] Export response", response.length);
                resolve(response.data);
            })
            .catch(error => {
                console.log("[CreativeService] Export error", error);
                reject(error.name);
            });
        });
    }

    // Review all creatives
    async reviewCreatives(acc_id: string, type: string, limit: number = 100, start_after?: string) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] reviewCreatives()");
            let requestPath = `/creative-analysis/review-creatives?acc_id=${acc_id}&type=${type}&limit=${limit}`;
            if(start_after && start_after != '') requestPath += `&start_after=${start_after}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] reviewCreatives response", response);
                resolve(response.data);
            })
            .catch(error => {
                console.log("[CreativeService] reviewCreatives error", error);
                reject(error.name);
            });
        });
    }

    // Get creative data
    // get-creative?acc_id=life-cykel-aus&id=IMG166
    async getCreative(acc_id: string, id: string) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] getCreative()");
            let requestPath = `/creative-analysis/get-creative?acc_id=${acc_id}&id=${id}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] getCreative response", response);
                resolve(response.data);
            })
            .catch(error => {
                console.log("[CreativeService] getCreative error", error);
                reject(error.name);
            });
        });
    }

    // Update creative data
    // update-creative?acc_id=shark-anz&id=IMG999&data=%7B%22creative_style%22%3A%22life%22%2C%22main_angle%22%3A%22Life%20is%20good!%22%7D
    async updateCreative(acc_id: string, id: string, data: any) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] updateCreative()");
            // URL encode the data to make it safe for URL
            let encodedData = encodeURIComponent(JSON.stringify(data));
            console.log("[CreativeService] updateCreative data", encodedData);
            let requestPath = `/creative-analysis/update-creative?acc_id=${acc_id}&id=${id}&data=${encodedData}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] updateCreative response", response);
                resolve(response.data);
            })
            .catch(error => {
                console.log("[CreativeService] updateCreative error", error);
                reject(error.name);
            });
        });
    }

    // Get creatives by ids
    // get-creatives?acc_id=life-cykel-aus&ids=[IMG166]
    async getCreatives(acc_id: string, ids: string[]) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] getCreatives()");
            let requestPath = `/creative-analysis/get-creatives?acc_id=${acc_id}&ids=${ids}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] getCreatives response", response);
                resolve(response.data);
            })
            .catch(error => {
                console.log("[CreativeService] getCreatives error", error);
                reject(error.name);
            });
        });
    }
    
    // Get signed URL for uploading a file to S3 temporary user storage
    async getS3UploadUrl(file: string, type: string) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] get()");
            this.loadingS3UploadUrl = true;      
            let requestPath = `/creative-analysis/s3-uploadurl?file=${file}&type=${type}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] Get response", response);   
                this.loadingS3UploadUrl = false;
                this.s3UploadUrl = response.data;
                resolve(this.s3UploadUrl!);
            })
            .catch(error => {
                console.log("[CreativeService] Get error", error.message);
                this.loadingS3UploadUrl = false;
                reject(error.message);
            });
        });
    }

    // Upload file to S3 user temporary storage using the signed URL
    async uploadFileToS3(signedUrl: string, file: File) {
        return new Promise<void>((resolve, reject) => {
            console.log("[CreativeService] uploadFileToS3()", signedUrl, file);
            const headers = {
                'Content-Type': file.type
            };
            this.httpService.put(signedUrl, file, { headers })
            .then(response => {
                console.log("[CreativeService] Upload response", response);
                resolve();
            })
            .catch(error => {
                console.log("[CreativeService] Upload error", error.message);
                reject(error.message);
            });
        });
    }

    // Import the creative from S3 user temporary storage to Firestore & S3 permanent creative analysis storage
    async importCreative(acc_id: string, creative: string, file: string, temp_file: string) {
        return new Promise<{url: string, thumbnail: string}>((resolve, reject) => {
            console.log("[CreativeService] importCreative()", acc_id, creative, file, temp_file);
            // If it's a carousel, get the last digit of the file name before extension
            let carouselIndex;
            if(creative.startsWith('CAR')) {
                carouselIndex = file.match(/(\d)(?=\.\w+$)/)?.[1];
                if(!carouselIndex) {
                    throw new Error("Invalid carousel index");
                }                
            }
            // Convert the file name to creative + file extension (IMG001.jpg, VID002.png...)
            let fileParts = file.split('.');
            file = creative + '.' + fileParts[fileParts.length - 1];
            console.log("[CreativeService] Calling s3-import", acc_id, creative, file, temp_file, carouselIndex);
            let requestPath = `/creative-analysis/s3-import?acc_id=${acc_id}&creative=${creative}&file=${file}&temp_file=${temp_file}`;
            if(carouselIndex) {
                requestPath += `&carindex=${carouselIndex}`;
            }
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] Import response", response);
                let url = response.data?.url;
                let thumbnail = response.data?.thumbnail;
                resolve({
                    url: url,
                    thumbnail: thumbnail
                });
            })
            .catch(error => {
                console.log("[CreativeService] Import error", error.message);
                reject(error.message);
            });
        });
    }

    // Request AI analysis of the creative
    async requestAnalysis(acc_id: string, creative: string, model: string = 'gemini') {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] requestAnalysis()");
            let requestPath = `/creative-analysis/request-analysis?acc_id=${acc_id}&id=${creative}&model=${model}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] Analysis response", response);
                resolve({
                    status: response.data?.status,
                    job: response.data?.job?.id
                });
            })
            .catch(error => {
                console.log("[CreativeService] Analysis error", error.message);
                reject(error.message);
            });
        });
    }

    // Request AI analysis of the video creative
    async requestVideoAnalysis(acc_id: string, creative: string) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] requestVideoAnalysis()");
            let requestPath = `/creative-analysis/request-analysis?acc_id=${acc_id}&id=${creative}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] Analysis response", response);
                resolve({
                    status: response.data?.status,
                    job: response.data?.job?.id
                });
            })
            .catch(error => {
                console.log("[CreativeService] Analysis error", error.message);
                reject(error.message);
            });
        });
    }

    // Request AI analysis of the carousel creative
    async requestCarouselAnalysis(acc_id: string, creativeId: string, creatives: string[]) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] requestCarouselAnalysis()");
            let requestPath = `/creative-analysis/classify-carousel-gemini?acc_id=${acc_id}&id=${creativeId}&creatives=${creatives}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] Analysis response", response);
                resolve(response.data);
            })
            .catch(error => {
                console.log("[CreativeService] Analysis error", error.message);
                reject(error.message);
            });
        });
    }

    // Check job status
    async checkJobStatus(acc_id: string, creative: string) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] checkJobStatus()");
            let requestPath = `/creative-analysis/check-analysis-job?acc_id=${acc_id}&id=${creative}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] Job response", response);
                let job = {
                    status: response.data?.status,
                    job: response.data?.id,
                    error: response.data?.error,
                    thumbnail: response.data?.thumbnail,
                    url: response.data?.url
                };
                resolve(job);
            })
            .catch(error => {
                console.log("[CreativeService] Job error", error.message);
                reject(error.message);
            });
        });
    }

    // Get the creative analysis results for an image
    async getCreativeAnalysisImage(acc_id: string, creative: string) {
        return new Promise<CreativeAnalysisImage>((resolve, reject) => {
            console.log("[CreativeService] getImageAnalysis()");
            let requestPath = `/creative-analysis/get-analysis?acc_id=${acc_id}&id=${creative}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] Analysis response", response);
                console.log("response.data?.classification", response.data?.classification);
                const formattedUserPrompt = this.formatNewLinesWithHtmlBreaks(response.data?.classification?.prompt?.user);
                let creativeAnalysis: CreativeAnalysisImage = {
                    id: response.data?.id,
                    thumbnail: response.data?.thumbnail,
                    url: response.data?.url,
                    status: response.data?.status,
                    classified: response.data?.classified,
                    classificationId: response.data?.classification?.id,
                    created: response.data?.classification?.created,
                    needsQa: response.data?.needs_qa,
                    needsExpertReview: response.data?.needs_expert_review,
                    needsExpertReviewReason: response.data?.needs_expert_review_reason,
                    mainAngle: response.data?.main_angle,
                    creativeStyle: response.data?.creative_style,
                    creativeFormat: response.data?.creative_format,
                    targetMarket: response.data?.target_market,
                    marketAwareness: response.data?.market_awareness,
                    usps: response.data?.usps,
                    discounts: response.data?.discounts,
                    ugc: response.data?.ugc,
                    headline: response.data?.headline,
                    personFeature: response.data?.person_feature,
                    callToAction: response.data?.call_to_action,
                    creativeStyleReasons: response.data?.classification?.meta_reasons?.creative_style?.reasons,
                    creativeStyleAlternatives: response.data?.classification?.meta_reasons?.creative_style?.alternatives,
                    discount: response.data?.classification?.discounts?.discount,
                    discountCode: response.data?.classification?.discounts?.discount_code,
                    mainAngleReasons: response.data?.classification?.meta_reasons?.main_angle?.reasons,
                    mainAngleAlternatives: response.data?.classification?.meta_reasons?.main_angle?.alternatives,
                    marketAwarenessReasons: response.data?.classification?.meta_reasons?.market_awareness?.reasons,
                    model: response.data?.classification?.model,
                    personFeatureVisible: response.data?.classification?.person_feature.visible,
                    personFeatureDescription: response.data?.classification?.person_feature?.description,
                    targetMarketDescription: response.data?.classification?.target_market.description,
                    targetMarketAgeCategory: response.data?.classification?.target_market.age_category,
                    targetMarketGender: response.data?.classification?.target_market.gender,
                    targetMarketReasons: response.data?.classification?.meta_reasons?.target_market?.reasons,
                    detectedTexts: response.data?.classification?.meta_frames_texts,
                    imageDescription: response.data?.classification?.meta_image_description,
                    reasons: response.data?.classification?.meta_reasons,
                    usageTokensTotal: response.data?.classification?.usage?.total_tokens,
                    prompt: {
                        system: response.data?.classification?.prompt?.system ? this.formatNewLinesWithHtmlBreaks(response.data?.classification?.prompt?.system) : '',
                        user: formattedUserPrompt
                    },
                    executionTime: {
                        model: response.data?.classification?.execution_time?.model,
                    },
                    rawOutput: response.data?.classification
                };
                resolve(creativeAnalysis);
            })
            .catch(error => {
                console.log("[CreativeService] Analysis error", error);
                reject(error.message);
            });
        });
    }
    
    // Get the creative analysis results for a video
    async getCreativeAnalysisVideo(acc_id: string, creative: string) {
        return new Promise<CreativeAnalysisVideo>((resolve, reject) => {
            console.log("[CreativeService] getCreativeAnalysisVideo()");
            let requestPath = `/creative-analysis/get-analysis?acc_id=${acc_id}&id=${creative}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] Analysis response", JSON.stringify(response));
                let prompts = [];
                if(response.data?.classification?.prompts) {
                    prompts = response.data?.classification?.prompts.map((prompt: any) => {
                        return {
                            type: prompt.type,
                            system: prompt.system ? this.formatNewLinesWithHtmlBreaks(prompt.system) : '',
                            user: prompt.user ? this.formatNewLinesWithHtmlBreaks(prompt.user) : ''
                        }
                    });
                }
                if(response.data?.classification?.prompt) {
                    prompts.push({
                        type: response.data?.classification?.prompt.type,
                        system: response.data?.classification?.prompt?.system ? this.formatNewLinesWithHtmlBreaks(response.data?.classification?.prompt?.system) : '',
                        user: response.data?.classification?.prompt?.user ? this.formatNewLinesWithHtmlBreaks(response.data?.classification?.prompt?.user) : ''
                    });
                }
                let creativeAnalysis: CreativeAnalysisVideo = {
                    id: response.data?.id,
                    thumbnail: response.data?.thumbnail,
                    url: response.data?.url,
                    status: response.data?.status,
                    classified: response.data?.classified,
                    classificationId: response.data?.classification?.id,
                    created: response.data?.classification?.created,
                    needsQa: response.data?.needs_qa,
                    needsExpertReview: response.data?.needs_expert_review,
                    needsExpertReviewReason: response.data?.needs_expert_review_reason,
                    mainAngle: response.data?.main_angle,
                    creativeStyle: response.data?.creative_style,
                    creativeFormat: response.data?.creative_format,
                    targetMarket: response.data?.target_market,
                    marketAwareness: response.data?.market_awareness,
                    usps: response.data?.usps,
                    discounts: response.data?.discounts,
                    ugc: response.data?.ugc,
                    audioHook: response.data?.audio_hook,
                    visualHook: response.data?.visual_hook,
                    textHook: response.data?.text_hook,
                    creator: response.data?.creator,
                    ageGroup: response.data?.age_group,
                    gender: response.data?.gender,
                    length: response.data?.length,
                    audioClose: response.data?.audio_close,
                    visualClose: response.data?.visual_close,
                    textClose: response.data?.text_close,
                    creativeStyleReasons: response.data?.classification?.meta_reasons?.creative_style?.reasons,
                    creativeStyleAlternatives: response.data?.classification?.meta_reasons?.creative_style?.alternatives,
                    discount: response.data?.classification?.discounts?.discount,
                    discountCode: response.data?.classification?.discounts?.discount_code,
                    mainAngleReasons: response.data?.classification?.meta_reasons?.main_angle?.reasons,
                    mainAngleAlternatives: response.data?.classification?.meta_reasons?.main_angle?.alternatives,
                    marketAwarenessReasons: response.data?.classification?.meta_reasons?.market_awareness?.reasons,
                    model: response.data?.classification?.model,
                    targetMarketAgeCategory: response.data?.classification?.target_market.age_category,
                    targetMarketGender: response.data?.classification?.target_market.gender,
                    targetMarketDescription: response.data?.classification?.target_market.description,
                    targetMarketReasons: response.data?.classification?.meta_reasons?.target_market?.reasons,
                    transcript: response.data?.classification?.transcript,
                    detectedTexts: response.data?.classification?.meta_frames_texts,
                    videoDescription: response.data?.classification?.meta_video_description,
                    reasons: response.data?.classification?.meta_reasons,
                    usageTokensTotal: response.data?.classification?.usage?.total_tokens,
                    prompts: prompts,
                    executionTime: {
                        ffmpeg: response.data?.classification?.execution_time?.ffmpeg,
                        model: response.data?.classification?.execution_time?.model,
                    },
                    rawOutput: response.data?.classification
                };
                resolve(creativeAnalysis);
            })
            .catch(error => {
                console.log("[CreativeService] Analysis error", error);
                reject(error.message);
            });
        });
    }

    // Get the creative analysis results for a carousel
    async getCreativeAnalysisCarousel(acc_id: string, creative: string) {
        return new Promise<CreativeAnalysisCarousel>((resolve, reject) => {
            console.log("[CreativeService] getCreativeAnalysisCarousel()");
            let requestPath = `/creative-analysis/get-analysis?acc_id=${acc_id}&id=${creative}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] Analysis response", JSON.stringify(response));
                let prompts = [];
                if(response.data?.classification?.prompts) {
                    prompts = response.data?.classification?.prompts.map((prompt: any) => {
                        return {
                            type: prompt.type,
                            system: prompt.system ? this.formatNewLinesWithHtmlBreaks(prompt.system) : '',
                            user: prompt.user ? this.formatNewLinesWithHtmlBreaks(prompt.user) : ''
                        }
                    });
                }
                if(response.data?.prompt) {
                    prompts.push({
                        type: response.data?.prompt.type,
                        system: response.data?.prompt.system ? this.formatNewLinesWithHtmlBreaks(response.data?.prompt.system) : '',
                        user: response.data?.prompt.user ? this.formatNewLinesWithHtmlBreaks(response.data?.prompt.user) : ''
                    });
                }
                let creativeAnalysis: CreativeAnalysisCarousel = {
                    id: response.data?.id,
                    thumbnail: response.data?.thumbnail,
                    url: response.data?.url,
                    status: response.data?.status,
                    classified: response.data?.classified,
                    classificationId: response.data?.classification?.id,
                    created: response.data?.classification?.created,
                    needsQa: response.data?.needs_qa,
                    needsExpertReview: response.data?.needs_expert_review,
                    needsExpertReviewReason: response.data?.needs_expert_review_reason,
                    mainAngle: response.data?.main_angle,
                    creativeStyle: response.data?.creative_style,
                    creativeFormat: response.data?.creative_format,
                    targetMarket: response.data?.target_market,
                    marketAwareness: response.data?.market_awareness,
                    usps: response.data?.usps,
                    discounts: response.data?.discounts,
                    ugc: response.data?.ugc,
                    numberOfTiles: response.data?.number_of_tiles,
                    creativeStyleReasons: response.data?.classification?.meta_reasons?.creative_style?.reasons,
                    creativeStyleAlternatives: response.data?.classification?.meta_reasons?.creative_style?.alternatives,
                    discount: response.data?.classification?.discounts?.discount,
                    discountCode: response.data?.classification?.discounts?.discount_code,
                    mainAngleReasons: response.data?.classification?.meta_reasons?.main_angle?.reasons,
                    mainAngleAlternatives: response.data?.classification?.meta_reasons?.main_angle?.alternatives,
                    marketAwarenessReasons: response.data?.classification?.meta_reasons?.market_awareness?.reasons,
                    model: response.data?.classification?.model,
                    targetMarketAgeCategory: response.data?.classification?.target_market.age_category,
                    targetMarketGender: response.data?.classification?.target_market.gender,
                    targetMarketDescription: response.data?.classification?.target_market.description,
                    targetMarketReasons: response.data?.classification?.meta_reasons?.target_market?.reasons,
                    detectedTexts: response.data?.classification?.meta_frames_texts,
                    usageTokensTotal: response.data?.classification?.usage?.total_tokens,
                    prompts: prompts,
                    executionTime: {
                        ffmpeg: response.data?.classification?.execution_time?.ffmpeg,
                        model: response.data?.classification?.execution_time?.model,
                    },
                    rawOutput: response.data?.classification
                };
                resolve(creativeAnalysis);
            })
            .catch(error => {
                console.log("[CreativeService] Analysis error", error);
                reject(error.message);
            });
        });
    }

    // Format new lines with HTML breaks. This is used to format the prompt text in the UI
    formatNewLinesWithHtmlBreaks(text: string): string {
        console.log("[CreativeService] formatNewLinesWithHtmlBreaks()", text);
        if(!text || typeof text !== 'string') {
            return '';
        } else {
            return text.replace(/\\n/g, '<br>');
        }
        
    }

    // List creative styles
    async listCreativeStyles(acc_id: string) {
        return new Promise<CreativeStyle[]>((resolve, reject) => {
            console.log("[CreativeService] listCreativeStyles()");
            let requestPath = `/creative-analysis/list-creative-styles?acc_id=${acc_id}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] List response", response);
                let creativeStyles: CreativeStyle[] = [];
                if(response.data) {
                    creativeStyles = response.data.map((style: any) => {
                        return {
                            id: style.id,
                            name: style.name,
                            description: style.description,
                            forImage: style.forImage,
                            forVideo: style.forVideo,
                            isCore: style.isCore,
                            visualCues: style.visualCues,
                            textCues: style.textCues,
                            updated: style.updated
                        }
                    });
                }
                resolve(creativeStyles);                
            })
            .catch(error => {
                console.log("[CreativeService] List error", error.name);
                reject(error.name);
            });
        });
    }

    async importCreativeStyles() {
        return new Promise<void>((resolve, reject) => {
            console.log("[CreativeService] importCreativeStyles()");
            let requestPath = `/creative-analysis/creative-styles-import`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] importCreativeStyles response", response);
                resolve();                
            })
            .catch(error => {
                console.log("[CreativeService] List error", error.name);
                reject(error.name);
            });
        });
    }

    // Get carousel creatives
    // get-carousel-creatives?acc_id=life-cykel-aus&id=IMG166
    async getCarouselCreatives(acc_id: string, id: string) {
        return new Promise<any>((resolve, reject) => {
            console.log("[CreativeService] getCarouselCreatives()");
            let requestPath = `/creative-analysis/get-carousel-creatives?acc_id=${acc_id}&id=${id}`;
            let requestParams = { params:
                new HttpParams()
            };
            this.apiService.get(requestPath, requestParams)
            .then(response => {
                console.log("[CreativeService] getCarouselCreatives response", response);
                resolve(response.data);
            })
            .catch(error => {
                console.log("[CreativeService] getCarouselCreatives error", error);
                reject(error.name);
            });
        });
    }
}